import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

// Check if we're in demo mode (Supabase URL is unreachable or localhost testing)
const isDemoMode = () => {
  const demoRegistration = localStorage?.getItem('demoRegistration');
  return demoRegistration === 'true';
};

// Demo user data
const getDemoUser = () => ({
  id: 'demo-user-' + Date.now(),
  email: localStorage?.getItem('ownerEmail') || 'owner@academy.com',
  user_metadata: {
    full_name: localStorage?.getItem('ownerName') || 'Demo Owner',
    role: 'owner'
  }
});

const getDemoProfile = (userId) => ({
  id: userId,
  full_name: localStorage?.getItem('ownerName') || 'Demo Owner',
  email: localStorage?.getItem('ownerEmail') || 'owner@academy.com',
  role: 'owner',
  organization_id: 'demo-org-' + Date.now(),
  phone: '',
  avatar_url: ''
});

const getDemoOrganization = () => ({
  id: 'demo-org-' + Date.now(),
  name: localStorage?.getItem('newAcademyName') || 'Demo Academy',
  subdomain: localStorage?.getItem('academySubdomain') || 'demo-academy',
  owner_id: 'demo-user-' + Date.now(),
  subscription_status: 'trial',
  subscription_plan: 'professional',
  trial_end: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)?.toISOString(),
  student_count: 0,
  max_students: 100,
  settings: {}
});

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [userOrganization, setUserOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [demoMode, setDemoMode] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Load user profile
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single()
        
        if (!profileError && profile) {
          setUserProfile(profile)
          
          // Load organization if user belongs to one
          if (profile?.organization_id) {
            const { data: org, error: orgError } = await supabase
              ?.from('organizations')
              ?.select('*')
              ?.eq('id', profile?.organization_id)
              ?.single()
            
            if (!orgError && org) {
              setUserOrganization(org)
            }
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setUserOrganization(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Check for demo mode first
    if (isDemoMode()) {
      setDemoMode(true);
      const demoUser = getDemoUser();
      const demoProfile = getDemoProfile(demoUser?.id);
      const demoOrg = getDemoOrganization();
      
      setUser(demoUser);
      setUserProfile(demoProfile);
      setUserOrganization(demoOrg);
      setLoading(false);
      return;
    }

    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      // If no session but demo mode is enabled (may have been set after initial load)
      if (!session && isDemoMode()) {
        setDemoMode(true);
        const demoUser = getDemoUser();
        const demoProfile = getDemoProfile(demoUser?.id);
        const demoOrg = getDemoOrganization();
        
        setUser(demoUser);
        setUserProfile(demoProfile);
        setUserOrganization(demoOrg);
        setLoading(false);
        return;
      }
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods - defined before value object
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, options = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({ 
        email, 
        password,
        options: {
          data: {
            role: 'owner',
            ...options?.data
          }
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Organization-specific methods
  const switchOrganization = async (organizationId) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      // Update user's organization
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({ organization_id: organizationId })
        ?.eq('id', user?.id)
        ?.select()
        ?.single()
      
      if (profileError) return { error: profileError }
      
      // Load new organization
      const { data: org, error: orgError } = await supabase
        ?.from('organizations')
        ?.select('*')
        ?.eq('id', organizationId)
        ?.single()
      
      if (orgError) return { error: orgError }
      
      // Update state
      setUserProfile(profile)
      setUserOrganization(org)
      
      return { data: { profile, organization: org }, error: null }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const refreshOrganization = async () => {
    if (!userProfile?.organization_id) return
    
    try {
      const { data: org, error } = await supabase
        ?.from('organizations')
        ?.select('*')
        ?.eq('id', userProfile?.organization_id)
        ?.single()
      
      if (!error && org) {
        setUserOrganization(org)
      }
      
      return { data: org, error }
    } catch (error) {
      return { error: { message: 'Failed to refresh organization data' } }
    }
  }

  // Helper methods for role checking
  const isOwner = () => userProfile?.role === 'owner'
  const isCoach = () => userProfile?.role === 'coach'
  const isStudent = () => userProfile?.role === 'student'
  const isSuperAdmin = () => userProfile?.is_super_admin === true
  const hasRole = (role) => userProfile?.role === role
  const hasAnyRole = (roles) => roles?.includes(userProfile?.role)

  // Organization helper methods
  const hasOrganization = () => !!userOrganization
  const getOrganizationId = () => userOrganization?.id
  const getOrganizationName = () => userOrganization?.name
  const getOrganizationSubdomain = () => userOrganization?.subdomain
  const isOrganizationOwner = () => userOrganization?.owner_id === user?.id
  const isInTrial = () => userOrganization && new Date(userOrganization.trial_end) > new Date()
  const getSubscriptionStatus = () => userOrganization?.subscription_status

  const value = {
    user,
    userProfile,
    userOrganization,
    loading,
    profileLoading,
    demoMode,
    signIn,
    signUp,
    signOut,
    updateProfile,
    switchOrganization,
    refreshOrganization,
    isAuthenticated: !!user,
    // Role helpers
    isOwner,
    isCoach,
    isStudent,
    isSuperAdmin,
    hasRole,
    hasAnyRole,
    // Organization helpers
    hasOrganization,
    getOrganizationId,
    getOrganizationName,
    getOrganizationSubdomain,
    isOrganizationOwner,
    isInTrial,
    getSubscriptionStatus
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}